// Shared display helpers for the Call Experience tab.

/** Sample values used to preview "{Placeholder}" text. */
export const SAMPLE_VALUES = {
  CompanyName: 'Acme Medical',
  HolidayName: 'Thanksgiving',
  CompanyPhoneNumber: '800-555-0100',
  CallbackCode: '48213',
  LastDayToOrder: 'December 20, 2026',
  StartDay: 'Monday',
  EndDay: 'Friday',
  StartTime: '9 AM',
  EndTime: '5 PM',
  CoachName: 'Sam',
  PatientName: 'Maria Lopez',
  Name: 'Maria Lopez',
  Supplies: 'CPAP',
  Items: 'a mask and standard tubing',
  AttemptsLeft: 'You have 2 attempts remaining.',
  0: 'a mask',
  1: '1',
};

/** Fills "{Name}" placeholders with sample values (the company's name when one is being viewed). */
export function fillSample(text, companyName) {
  const values = companyName ? { ...SAMPLE_VALUES, CompanyName: companyName } : SAMPLE_VALUES;
  return (text || '').replace(/\{(\w+)\}/g, (m, name) => values[name] ?? m);
}

/** Renders text with its "{Placeholder}" tokens highlighted. */
export function Highlighted({ text }) {
  const parts = (text || '').split(/(\{\w+\})/g);
  return parts.map((part, i) =>
    /^\{\w+\}$/.test(part) ? <span key={i} className="cx-ph">{part}</span> : part);
}

const ORIGINS = {
  company: { cls: 'cx-badge-co', label: 'custom', title: 'Own wording for this company or group' },
  parent: { cls: 'cx-badge-par', label: 'via group', title: 'Inherited from the parent group' },
  global: { cls: 'cx-badge-gl', label: 'default', title: 'Global default in the database' },
  fallback: { cls: 'cx-badge-gl', label: 'code default', title: 'No wording in the database; the built-in fallback is spoken' },
  builtin: { cls: 'cx-badge-bi', label: 'built-in', title: 'Built into the code; the same for every company' },
};

export function OriginBadge({ origin }) {
  const o = ORIGINS[origin] || ORIGINS.global;
  return <span className={`cx-badge ${o.cls}`} title={o.title}>{o.label}</span>;
}

/** Reads text aloud with the browser's voice, as a stand-in for Google TTS. */
export function speak(text, lang) {
  if (!('speechSynthesis' in window)) return false;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = lang === 'es' ? 'es-US' : 'en-US';
  window.speechSynthesis.speak(utterance);
  return true;
}

const KEY_WORDS = {
  zero: '0', one: '1', two: '2', three: '3', four: '4', five: '5', six: '6', seven: '7', eight: '8', nine: '9',
  cero: '0', uno: '1', dos: '2', tres: '3', cuatro: '4', cinco: '5', seis: '6', siete: '7', ocho: '8', nueve: '9',
  pound: '#', star: '*',
};
const PRESS = /\b(press|presione|pulse|marque|oprima)\b/i;
const KEY_TOKEN = /(?:^|[^\w{])([0-9#*])(?![\w}])|\b(zero|one|two|three|four|five|six|seven|eight|nine|cero|uno|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|pound|star)\b/gi;

/**
 * Keys a line tells the caller to press: every single digit or number word (English or Spanish) in a
 * sentence that says "press" / "presione". "Press 1 for yes, or 2 for no." gives 1 and 2.
 */
export function mentionedKeys(text) {
  const keys = new Set();
  (text || '').split(/(?<=[.!?])\s+/).forEach((sentence) => {
    if (!PRESS.test(sentence)) return;
    for (const m of sentence.matchAll(KEY_TOKEN)) keys.add(m[1] ?? KEY_WORDS[m[2].toLowerCase()]);
  });
  return [...keys];
}

/** The keys callers can press where a line is used (union across usages); null when any key is accepted or none apply. */
export function acceptedKeys(usage) {
  const keys = new Set((usage || []).flatMap((u) => u.keys || []));
  if (keys.size === 0 || keys.has('any')) return null;
  return [...keys].sort();
}

export const journeyKey = (j) => `${j.id}:${j.productLineId ?? ''}`;

export const JOURNEY_ICONS = { inbound: '📥', 'outbound-coach': '📤', 'resupply-ivr': '🤖' };
